<script setup lang="ts">
import { computed, defineProps } from "vue";
import { DestinyMatrix } from "@/types/destinyMatrix";
import CardComponent from "@/views/Calculators/DestinyMatrix/components/CardComponent.vue";
import { useI18n } from "vue-i18n";
import { Chakra } from "@/constants/destinyMatrix";

const props = defineProps<{
  matrix: DestinyMatrix | undefined;
}>();

const { t } = useI18n();

function reduce(num: number): number {
  if (num <= 22) {
    return num;
  }

  while (num > 22) {
    let sum = 0;
    while (num > 0) {
      sum += num % 10;
      num = Math.floor(num / 10);
    }
    num = sum;
  }

  return num;
}

const tableContent = computed(() => {
  const matrix = props.matrix;
  if (matrix === undefined) return [];

  return [
    {
      chakraKey: "sahasrara",
      physics: matrix.r1.l,
      energy: matrix.r1.t,
      emotion: reduce(matrix.r1.l + matrix.r1.t),
    },
    {
      chakraKey: "ajna",
      physics: matrix.r2.l,
      energy: matrix.r2.t,
      emotion: reduce(matrix.r2.l + matrix.r2.t),
    },
    {
      chakraKey: "vishuddha",
      physics: matrix.r3.l,
      energy: matrix.r3.t,
      emotion: reduce(matrix.r3.l + matrix.r3.t),
    },
    {
      chakraKey: "anahata",
      physics: matrix.middle_points.l,
      energy: matrix.middle_points.t,
      emotion: reduce(matrix.middle_points.l + matrix.middle_points.t),
    },
    {
      chakraKey: "manipura",
      physics: matrix.center,
      energy: matrix.center,
      emotion: reduce(matrix.center + matrix.center),
    },
    {
      chakraKey: "swadhisthana",
      physics: matrix.r3.r,
      energy: matrix.r3.b,
      emotion: reduce(matrix.r3.r + matrix.r3.b),
    },
    {
      chakraKey: "muladhara",
      physics: matrix.r1.r,
      energy: matrix.r1.b,
      emotion: reduce(matrix.r1.r + matrix.r1.b),
    },
  ];
});
const summaryRow = computed(() => {
  let physicsSum = 0;
  let energySum = 0;
  let emotionSum = 0;
  for (const contentRow of tableContent.value) {
    physicsSum += contentRow.physics;
    energySum += contentRow.energy;
    emotionSum += contentRow.emotion;
  }

  return {
    physics: reduce(physicsSum),
    energy: reduce(energySum),
    emotion: reduce(emotionSum),
  };
});
</script>

<template>
  <CardComponent>
    <template #content>
      <div class="flex flex-col gap-2 text-left">
        <p class="font-bold text-base lg:text-lg">
          {{ t("calculators.destiny-matrix.health-card.title") }}
        </p>
        <p class="text-brown text-xs lg:text-sm mb-2">
          {{ t("calculators.destiny-matrix.health-card.table-title") }}
        </p>
        <table class="text-center">
          <thead class="[&_th]:border-2 [&_th]:border-white">
            <tr class="bg-hover h-12 text-xs lg:text-sm font-bold text-brown">
              <td>
                {{ t("calculators.destiny-matrix.health-card.chakra-name") }}
              </td>
              <td>{{ t("calculators.destiny-matrix.health-card.physics") }}</td>
              <td>{{ t("calculators.destiny-matrix.health-card.energy") }}</td>
              <td>{{ t("calculators.destiny-matrix.health-card.emotion") }}</td>
            </tr>
          </thead>
          <tbody
            class="[&_td]:border-2 [&_td]:border-white text-sm lg:text-base"
          >
            <tr
              v-for="tableRow in tableContent"
              :key="tableRow.chakraKey"
              :style="'background-color: ' + Chakra[tableRow.chakraKey]"
              :class="
                tableRow.chakraKey === 'manipura' ? 'text-black' : 'text-white'
              "
              class="h-12"
            >
              <td>
                {{
                  t(
                    `calculators.destiny-matrix.health-card.${tableRow.chakraKey}`
                  )
                }}
              </td>
              <td>{{ tableRow.physics }}</td>
              <td>{{ tableRow.energy }}</td>
              <td>{{ tableRow.emotion }}</td>
            </tr>
            <tr class="bg-gray-300 h-12 font-black">
              <td>{{ t("calculators.destiny-matrix.health-card.summary") }}</td>
              <td>{{ summaryRow.physics }}</td>
              <td>{{ summaryRow.energy }}</td>
              <td>{{ summaryRow.emotion }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </CardComponent>
</template>
